<template>
  <div class="max-width">
    <v-row v-if="newMessagesFrom.length > 0" class="justify-end">
      <v-col cols="12">
        <v-alert type="info" dismissible @input="newMessagesFrom = []">
          New messages from: {{ newMessagesFrom.join(", ") }}
        </v-alert>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" align="center" justify="center">
        <v-row justify="end">
          <v-col md="6">
            <div class="d-flex align-center justify-center">
              <h2 align="center" justify="center">Club</h2>
              <div v-if="this.subscriptions && this.subscriptions.length > 0" class="ml-2 caption">
                <vue-json-to-csv :json-data="CSVData" csv-title="club_clients">
                  <v-btn x-small>
                    Export
                  </v-btn>
                </vue-json-to-csv>
              </div>
            </div>
          </v-col>
          <v-col md="3" class="d-flex justify-end">
            <v-checkbox
              class="mr-3"
              v-model="hideResolved"
              @change="applyFilters(subscriptions)"
              label="Show unresolved chats"
            />
          </v-col>
        </v-row>
        <v-data-table
          height="1000"
          :headers="table.headers"
          :options="table.options"
          :items="applyFilters(subscriptions)"
          :item-class="rowClass"
          :search="search"
          :show-group-by="true"
          :hide-default-footer="true"
          disable-pagination
          fixed-header
          dense
          :loading="loading"
          :custom-sort="customSort"
        >
          <template v-slot:top>
            <v-text-field v-model="search" class="mx-4 mt-0 pt-0">
              <template v-slot:label>
                <v-icon color="secondary"> mdi-magnify </v-icon>
                Search
              </template>
            </v-text-field>
          </template>

          <template v-slot:[`item.profile_pic`]="props">
            <profile-pic :profilePic="props.item.profile_pic" :userId="props.item.userId" />
          </template>

          <template v-slot:[`header.client_name`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.client_name`]="props">
            <client-name
              :client-id="props.item.userId"
              :dob="props.item.dateOfBirth"
              :profileLink="props.item.profile_link"
              :subscriptionEndDate="props.item.subscriptionEndDate"
              :clientName="props.item.client_name"
              :username="props.item.username"
              :contactLink="props.item.contact_link"
              :billingDate="props.item.billingDate"
              :lastActive="props.item.client.userProfile.lastActive"
              :nextSubscription="props.item.nextSubscription"
              :upcomingPayment="props.item.upcomingPayment"
              :coachingChat="props.item.client.userProfile.coachingChat"
              :supportChat="props.item.client.userProfile.supportChat"
              :coachId="coachId"
              :subscriptionSurveyId="props.item.subscriptionSurveyId"
              :weeksOnProgram="props.item.weeksOnProgram"
            />
          </template>

          <template v-slot:[`item.stickyNote`]="props">
            <sticky-note :note="props.item.stickyNote" />
          </template>

          <template v-slot:[`header.activeWorkoutBlocks`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.activeWorkoutBlocks`]="props">
            <active-workout-blocks :workoutBlocks="props.item.activeWorkoutBlocks" :nextWorkoutBlocks="props.item.nextWorkoutBlocks"  />
          </template>

          <template v-slot:[`header.nutritionPlan`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.nutritionPlan`]="props">
            <span class="block caption">
              <b>Plan:</b>
              <a :href="props.item.nutritionPlan.pdf" target="_blank">{{ props.item.nutritionPlan.title }}</a>
            </span>
            <span class="block caption"> <b>Block:</b> {{ props.item.nutritionBlock }} </span>
          </template>

          <template v-slot:[`item.engagementScore`]="props">
            <engagement-score-circle :engagement-score="props.item.engagementScore" />
          </template>

          <template v-slot:[`header.lastContact`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>

          <template v-slot:[`item.lastContact`]="props">
            <span :class="getLastContactClass(props.item.lastContact)">
              {{ props.item.lastContact | moment("from", "now") }}
            </span>
          </template>

          <template v-slot:[`header.lastMessage`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.lastMessage`]="props">
            <last-message-column @viewed-message="onMessageViewed" :message="props.item.lastMessage" />
          </template>
          <template v-slot:[`header.resolved`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.resolved`]="props">
            <resolve-message-btn :chat="props.item.chat" :resolved="props.item.resolved" @reload="reloadData" />
          </template>

          <template v-slot:[`header.lastUserAssessmentDate`]="props">
            <table-header :text="props.header.text" :tooltip="props.header.help" />
          </template>
          <template v-slot:[`item.lastUserAssessmentDate`]="props">
            <span
              v-if="props.item.lastUserAssessmentDate"
              :class="getAssessmentClass(props.item.lastUserAssessmentDate)"
              >{{ formatAssessmentDate(props.item.lastUserAssessmentDate) }}</span
            >
            <span v-else class="red--text">No check in!</span>
          </template>

          <template v-slot:[`item.controls`]="props" width="100">
            <v-lazy
              :options="{
                threshold: 0.9,
              }"
            >
              <tool-buttons
                :coachId="coachId"
                :userId="props.item.userId"
                :username="props.item.username"
                :clientName="props.item.client_name"
                :activityLoggedToday="props.item.activity_logged_today"
                class="text-center mb-2 mt-1"
                :injectedData="props.item.user"
              />
            </v-lazy>
            <v-lazy
              :options="{
                threshold: 0.9,
              }"
            >
              <client-week-dots :userId="props.item.userId" show-arrows class="mb-1" />
            </v-lazy>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import MetricMixin from "@/components/shared/mixins/MetricMixin";
import CoachingDashboardBase from "@/components/dashboard/mixins/CoachingDashboardBase";

export default {
  name: "CoachingClubSubscription",
  props: {
    coachId: {
      type: String,
      required: false,
    },
  },
  mixins: [CoachingDashboardBase, MetricMixin],
  data() {
    return {
      encodedCoachId: btoa("UserNode:" + this.coachId),
      search: "",
      defaultCompleteSelectionText: "Hide rows",
      selectedUsername: "",
      selectedFullname: "",
      queryProgramType: "",
      subscriptions: [],
      productType: "club",
      surveyType: "Club_Cancel",
      stages: null,
      table: {
        options: {
          showGroupBy: true,
          multiSort: false,
        },
        headers: [
          { text: "", value: "profile_pic", groupable: false },
          { text: "Client", value: "client_name", groupable: false },
          { text: "", value: "stickyNote", groupable: false },
          {
            text: "",
            value: "controls",
            sortable: false,
            groupable: false,
            width: 120,
          },
          { text: "Blocks", value: "activeWorkoutBlocks", groupable: false, help: "Current workout blocks" },
          { text: "Nutrition", value: "nutritionPlan", groupable: false },
          { text: "7 day engagement", value: "engagementScore", groupable: false },
          {
            text: "Last Contact",
            value: "lastContact",
            groupable: false,
            help: "Last message sent in coach chat",
          },
          { text: "Last Message", value: "lastMessage", groupable: false },
          { text: "", value: "resolved", groupable: false, help: "Resolve any outstanding messages from this client" },
          {
            text: "Last Checkin",
            value: "lastUserAssessmentDate",
            groupable: false,
            help: "Last User Checkin",
          },
        ],
      },
    };
  },
  methods: {
    getLastContactClass(date) {
      if (!date) {
        return "";
      }
      const weeks = this.$moment().diff(date, "days");
      if (weeks < 3) {
        return "green--text";
      }
      if (weeks < 8) {
        return "orange--text";
      }
      return "red--text";
    },
    getAssessmentClass(date) {
      if (!date) {
        return "";
      }
      const weeks = this.$moment().diff(date, "weeks");
      if (weeks < 5) {
        return "green--text";
      }
      if (weeks < 9) {
        return "orange--text";
      }
      return "red--text";
    },
    formatAssessmentDate(date) {
      if (!date) {
        return "";
      }
      return this.$moment(date).fromNow();
    },
  },
};
</script>

<style lang="scss" scoped>
.max-width {
  width: 100%;
}

.exercise-dot {
  position: absolute;
  top: 16px;
  background-color: var(--v-exercise-base);
  border: 1px solid white;
  border-radius: 100%;
  width: 22px;
  height: 22px;
}
.block {
  display: block;
}
</style>
